<template>
  <div class="card h-100 border">
    <div class="card-header">
      <h5 class="mb-0">{{ prompt.id }}</h5>
    </div>
    <div class="card-body bg-body-tertiary">
      <p class="fs-10 text-body-primary mb-0">{{ prompt.subtitle }}</p>
      <!-- KPI numbers are real backtest figures (TradingView-style Max Drawdown). -->
      <div class="d-flex flex-wrap gap-2 gap-xl-3_ sm-gap-3_ mt-2 me-2 align-items-center" style="row-gap: 0.25rem;">
        <div><span class="text-body-secondary">PnL</span> <span class="badge rounded-pill badge-subtle-success">{{ prompt.kpis.pnl }}</span></div>
        <div><span class="text-body-secondary">Max DD</span> <span class="badge rounded-pill badge-subtle-danger">{{ prompt.kpis.maxDrawdown }}</span></div>
        <div><span class="text-body-secondary">Trades</span> <span class="badge rounded-pill badge-subtle-primary" title="Trading fees are included in the PnL.">{{ prompt.kpis.trades }}</span> <span class="badge rounded-pill badge-subtle-primary">{{ prompt.kpis.direction }}</span> </div>
        <div><span class="text-body-secondary">Chart</span> <span class="badge rounded-pill badge-subtle-primary">{{ prompt.kpis.timeframe }}</span></div>
        <div><span class="text-body-secondary">Range</span> <span class="badge rounded-pill badge-subtle-primary">{{ prompt.kpis.period }}</span></div>
      </div>
      <img v-if="prompt.equityImage" :src="`${prompt.equityImage}?v=${encodeURIComponent(prompt.lastUpdated)}`"
           class="w-100 h-auto rounded mt-3" loading="lazy" width="1600" height="400"
           @error="$event.target.style.display = 'none'">
      <div class="fs-10 mt-3 mt-xl-2">
        <span class="text-body-primary">Updated</span> <span class="badge rounded-pill badge-subtle-secondary" title="Strategy prompt last updated datetime (UTC)">{{ prompt.lastUpdated }} UTC</span>
        <p class="fs-11 text-body-primary mt-1 mb-0">
          Past performance is not a guarantee for future results.
        </p>
      </div>
    </div>
    <!-- Bottom row: pick the Bot this prompt gets personalized for, right in context.
         Access gating is NOT handled here — the page passes it in via `disabled`. -->
    <div class="card-footer py-2">
      <div v-if="disabled" class="d-flex justify-content-end">
        <CopyAiPromptButton disabled prompt="" :title="disabledReason" />
      </div>
      <div v-else-if="bots === null" class="d-flex align-items-center justify-content-between gap-2">
        <span class="fs-10 text-body-secondary">Loading your Bots...</span>
        <CopyAiPromptButton class="flex-shrink-0" disabled prompt="" title="Loading your Bots..." />
      </div>
      <div v-else-if="bots.length === 0" class="d-flex align-items-center justify-content-between gap-2">
        <span class="fs-10 text-body-secondary">
          You don't have a Bot yet.
          <router-link :to="{name: 'MyBots', params: {}}">Create your first Bot</router-link>
        </span>
        <CopyAiPromptButton class="flex-shrink-0" disabled prompt="" title="Create a Bot first, so the prompt can be personalized with its Bot ID." />
      </div>
      <div v-else-if="activeBots.length === 0" class="d-flex align-items-center justify-content-between gap-2">
        <span class="fs-10 text-body-secondary">
          All your Bots are paused.
          <router-link :to="{name: 'MyBots', params: {}}">Activate a Bot</router-link>
        </span>
        <CopyAiPromptButton class="flex-shrink-0" disabled prompt="" title="Activate a Bot first, so the prompt can be personalized with its Bot ID." />
      </div>
      <!-- align-items-stretch makes the copy button as tall as the form-floating selector -->
      <div v-else class="d-flex flex-wrap align-items-stretch gap-2">
        <div class="flex-grow-1" style="min-width: 200px;">
          <SingleSelectAutocomplete
            v-model="selectedBot"
            :options="activeBots"
            :option-label="bot => bot.title"
            :option-key="bot => bot.id"
            label="Select your Bot">
            <template #label>Select your Bot</template>
            <template #option="{ item }">
              <BotSelectOption :bot="item" />
            </template>
            <template #selected="{ item }">
              <BotSelectOption :bot="item" />
            </template>
          </SingleSelectAutocomplete>
        </div>
        <CopyAiPromptButton class="flex-shrink-0 ms-auto"
                            :prompt="() => selectedBot ? prompt.build(selectedBot.id, prompt.id) : ''"
                            :disabled="!selectedBot"
                            :title="copyTitle" />
      </div>
    </div>
  </div>
</template>

<script>
import BotSelectOption from '@/components/BotSelectOption'
import CopyAiPromptButton from '@/components/CopyAiPromptButton'
import SingleSelectAutocomplete from '@/components/SingleSelectAutocomplete'

// One AI strategy prompt box: title, KPIs, equity chart, and its own Bot selector +
// "Copy AI Prompt" action, so users pick the Bot right where they copy the prompt.
// Each card instance keeps its own selection.
export default {
  name: 'AiPromptCard',
  components: {
    BotSelectOption,
    CopyAiPromptButton,
    SingleSelectAutocomplete
  },
  props: {
    // One entry from utils/mcpPrompts.js
    prompt: {
      type: Object,
      required: true
    },
    // The user's bots; null means "still loading"
    bots: {
      type: Array,
      default: null
    },
    // Set by the page when the user may not use the prompts (e.g. no strategy access)
    disabled: {
      type: Boolean,
      default: false
    },
    // Tooltip shown on the disabled copy button when `disabled` is set
    disabledReason: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      selectedBot: null
    }
  },
  computed: {
    activeBots () {
      return (this.bots || []).filter(b => b.enabled)
    },
    copyTitle () {
      if (!this.selectedBot) return 'Select one of your Bots first, so the prompt can be personalized with its Bot ID.'
      return 'Copy this strategy prompt, personalized with the ID of your selected Bot.'
    }
  },
  watch: {
    // Bots can arrive after mount (async fetch) or change (bot paused elsewhere):
    // preselect when there is exactly one active Bot, and drop a selection that
    // is no longer active.
    activeBots: {
      immediate: true,
      handler (bots) {
        if (this.selectedBot && !bots.find(b => b.id === this.selectedBot.id)) {
          this.selectedBot = null
        }
        if (!this.selectedBot && bots.length === 1) {
          this.selectedBot = bots[0]
        }
      }
    }
  }
}
</script>
